// src/sim/foxguard/detection.ts — P3 four-condition accumulator (GAME-13, GAME-15, D-15..D-18).
//
// Invariants:
//   - Pure: no state kept between calls; accumulator lives on Entity.foxguardConditionsStreakStartTick.
//   - Caller must gate on world.round.phase === 'active' (Pitfall 4).
//   - Condition 4 is the P3 stub; P4 perception.ts replaces with a real FOV+hearing check.
//   - Reference-reuse guard (Pitfall 11): updateFoxguardStreak returns the SAME entity
//     reference when nothing changed, so callers can diff by identity.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-15..D-18.

import type { Entity, WorldState } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import { zoneAt, zoneCenter } from '../map/zones';
// P7 POL-06 / D-12: read foxguard thresholds through the tuning cache
// (code constants in ./constants are the fallback defaults).
import { getTuning } from '../content/cache';

/**
 * D-15 four-condition evaluator. Returns true only when ALL conditions pass
 * at the given tick. The caller owns round-phase gating (Pitfall 4).
 *
 * Conditions (all must hold):
 *   1. `status==='escorting'` OR (`recentDropOffTick!==null` AND
 *      `currentTick - recentDropOffTick <= P3_FOXGUARD_RECENT_DROPOFF_TICKS`)
 *   2. squared distance from plinth centre ≤ (P3_FOXGUARD_NEAR_BASE_TILES * tileWidth)²
 *   3. velocity magnitude < P3_FOXGUARD_VELOCITY_PX_PER_S  (lastPosition=null → 0)
 *   4. no free non-hidden hider outside the plinth zone
 */
export function evaluateFoxguardConditions(
  hunter: Entity,
  world: WorldState,
  mapData: MapData,
  dt: number,
): boolean {
  const tuning = getTuning();

  // Condition 1: carrying OR recently dropped off.
  const currentTick = world.tick;
  const recent =
    hunter.recentDropOffTick !== null &&
    currentTick - hunter.recentDropOffTick <= tuning.foxguardRecentDropoffTicks;
  if (hunter.status !== 'escorting' && !recent) return false;

  // Condition 2: within N tiles of plinth centre.
  const plinth = mapData.zones.get('plinth');
  if (!plinth) return false;
  const centre = zoneCenter(plinth);
  const dx = hunter.position.x - centre.x;
  const dy = hunter.position.y - centre.y;
  const nearPx = tuning.foxguardNearBaseTiles * mapData.tileWidth;
  if (dx * dx + dy * dy > nearPx * nearPx) return false;

  // Condition 3: velocity below threshold. lastPosition=null → velocity=0
  // (counts as stationary; common on the very first tick after spawn/team-switch).
  if (hunter.lastPosition !== null && dt > 0) {
    const vx = (hunter.position.x - hunter.lastPosition.x) / dt;
    const vy = (hunter.position.y - hunter.lastPosition.y) / dt;
    const vCap = tuning.foxguardVelocityPxPerS;
    if (vx * vx + vy * vy >= vCap * vCap) return false;
  }

  // Condition 4 (P4): no high/medium-confidence blackboard entry outside plinth.
  // Replaces the P3 stub that iterated raw world.entities (Pitfall 4).
  const bb = world.teamBlackboards.hunter;
  for (const entry of bb.entries) {
    if (entry.confidence === 'low') continue;
    if (zoneAt(entry.position, mapData.zones) !== 'plinth') return false;
  }
  return true;
}

export interface StreakResult {
  next: Entity;
  graceElapsed: boolean;
}

/**
 * D-18 streak accumulator update. Pure. If conditionsMet and streak is null,
 * start it. If !conditionsMet, clear. If streak length ≥ grace, signal
 * graceElapsed and reset streak so the next shout requires a fresh accumulation.
 *
 * Pitfall 11: when nothing changes, returns the SAME entity reference so callers
 * can use identity equality to skip writes.
 */
export function updateFoxguardStreak(
  hunter: Entity,
  conditionsMet: boolean,
  currentTick: number,
): StreakResult {
  if (!conditionsMet) {
    if (hunter.foxguardConditionsStreakStartTick === null) {
      return { next: hunter, graceElapsed: false };
    }
    return { next: { ...hunter, foxguardConditionsStreakStartTick: null }, graceElapsed: false };
  }
  if (hunter.foxguardConditionsStreakStartTick === null) {
    return {
      next: { ...hunter, foxguardConditionsStreakStartTick: currentTick },
      graceElapsed: false,
    };
  }
  const streakLen = currentTick - hunter.foxguardConditionsStreakStartTick;
  if (streakLen >= getTuning().foxguardGraceTicks) {
    // Fire + reset so next shout requires fresh accumulation.
    return { next: { ...hunter, foxguardConditionsStreakStartTick: null }, graceElapsed: true };
  }
  return { next: hunter, graceElapsed: false };
}
